// src/pages/SimpleDashboardPage.tsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, getFullImageUrl } from '../lib/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AboutUsSection } from '../components/AboutUsSection';
import { useAuthStore } from '../stores/useAuthStore';
import { useClientUnpaidOrders, useClientAcceptedOrders } from '../hooks/queries/useOrders';
import { ChevronLeft, Clock, ShoppingBag, PackageSearch, RefreshCw, ChevronRight } from 'lucide-react';
import KIERO_LOGO from '../media/KIERO.png';

export function SimpleDashboardPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { identifier, validateSession: authValidateSession, tenant: storeTenant } = useAuthStore();

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState<any>(null);
    const [tenant, setTenant] = useState<any>(null);
    const [showAboutSection, setShowAboutSection] = useState(false);

    // Scroll detection para mostrar sección About Us
    useEffect(() => {
        const handleScroll = () => {
            setShowAboutSection(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Data de tracking
    const { data: unpaidOrdersData, refetch: refetchUnpaid } = useClientUnpaidOrders();
    const { data: acceptedOrdersData, refetch: refetchAccepted } = useClientAcceptedOrders();

    const refetchAll = () => {
        refetchUnpaid();
        refetchAccepted();
    };

    // Mapear ordenes de forma única evitando duplicados entre listas
    const uniqueOrdersMap = new Map();
    [...(unpaidOrdersData?.orders || []), ...(acceptedOrdersData?.orders || [])].forEach(order => {
        if (order.uuid) uniqueOrdersMap.set(order.uuid, order);
    });

    const allOrders = Array.from(uniqueOrdersMap.values())
        .sort((a, b) => new Date(b.created_at || new Date()).getTime() - new Date(a.created_at || new Date()).getTime());

    useEffect(() => {
        if (!token) {
            navigate('/error');
            return;
        }

        const init = async () => {
            try {
                // Asegurar que tenemos sesión válida usando el store
                const session = await authValidateSession(identifier || 'anonymous', token);

                if (!session || !apiClient.getSessionToken()) {
                    navigate('/error');
                    return;
                }

                // Cargar config
                const configResp: any = await apiClient.getConfig();
                if (configResp?.success) {
                    const configData = configResp.config || {};
                    const tenantData = configResp.tenant || {};
                    setConfig(configData);
                    setTenant({
                        name: configData.tenant_name || 'ComandaYa',
                        logotype: configData.logotype || '',
                        banner: configData.banner || '',
                        about_us: tenantData.about_us || configData.business_description || null,
                        instagram: tenantData.instagram || null,
                        facebook: tenantData.facebook || null,
                        tiktok: tenantData.tiktok || null,
                    });
                }
            } catch (err) {
                console.error('Error in SimpleDashboardPage init:', err);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [token]);

    const currencySymbol = config?.currency_symbol || '$';

    if (loading) return <LoadingSpinner message="Cargando tus pedidos..." />;

    return (

        <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-bg-page)' }}>
            {/* Header con Banner Reducido */}
            <div className="relative h-40 overflow-hidden">
                {tenant?.banner ? (
                    <img src={getFullImageUrl(tenant.banner)} alt={tenant.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }} />
                )}
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
                <div className="absolute inset-0 flex items-center px-6 pt-4">
                    <button
                        onClick={() => navigate(`/simple-menu/${token}`)}
                        className="p-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-2xl text-white border border-white/10 transition-all mr-4"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-black text-white drop-shadow-lg">Mis Pedidos</h1>
                        <p className="text-white/70 text-xs font-medium">{tenant?.name}</p>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 -mt-6 rounded-t-[2.5rem] px-4 py-8 relative z-10 space-y-6 max-w-md mx-auto w-full" style={{ backgroundColor: 'var(--color-bg-page)' }}>

                {/* Status Section */}
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest px-2">Estado actual</h2>
                    <button
                        onClick={refetchAll}
                        className="p-2 text-accent transition-colors"
                        style={{ color: 'var(--color-accent-500)' }}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>

                {allOrders.length === 0 ? (
                    <div className="py-20 text-center space-y-4">
                        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800/50 rounded-[2rem] flex items-center justify-center mx-auto mb-4 border border-gray-200/20">
                            <PackageSearch className="w-10 h-10 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">No hay pedidos registrados</h3>
                        <p className="text-gray-500 text-sm max-w-[240px] mx-auto">Cuando realices un pedido aparecerá aquí para que puedas seguir su estado.</p>
                        <button
                            onClick={() => navigate(`/simple-menu/${token}`)}
                            className="btn-accent px-8 py-3 rounded-2xl font-bold shadow-lg active:scale-95 transition-all mt-4"
                            style={{ boxShadow: '0 10px 15px -3px var(--color-accent-500)' }}
                        >
                            Ver el Catálogo
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4 pb-32">
                        {allOrders.map((order: any) => {
                            let statusColor = 'text-gray-500 bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
                            let statusText = 'Pendiente';
                            let statusIcon = <Clock className="w-4 h-4" />;

                            if (order.status === 'PREPARING') {
                                statusColor = 'text-amber-500 bg-amber-50 dark:bg-amber-900/20 border-amber-200/50 dark:border-amber-800/50';
                                statusText = 'En cocina';
                                statusIcon = <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />;
                            } else if (order.status === 'READY') {
                                statusColor = 'text-green-500 bg-green-50 dark:bg-green-900/20 border-green-200/50 dark:border-green-800/50';
                                statusText = '¡Listo para retirar!';
                                statusIcon = <CheckCircle2 className="w-4 h-4" />;
                            } else if (order.status === 'DELIVERED') {
                                statusColor = 'text-blue-500 bg-blue-50 dark:bg-blue-900/20 border-blue-200/50 dark:border-blue-800/50';
                                statusText = 'Entregado';
                            }

                            return (
                                <div key={order.uuid} className="group bg-white dark:bg-gray-800/40 backdrop-blur-md rounded-[2rem] p-5 shadow-sm border border-gray-100 dark:border-white/5 transition-all hover:shadow-md">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Orden #{order.order_number}</span>
                                            <div className={`flex items-center gap-2 px-3 py-1.5 mt-1 rounded-xl text-[10px] font-black uppercase tracking-wider border shadow-sm ${statusColor}`}>
                                                {statusIcon}
                                                {statusText}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center gap-1.5 text-gray-400 justify-end mb-1">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span className="text-xs font-medium">
                                                    {new Date(order.created_at || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            {order.order_data?.pickup_name && (
                                                <span className="text-[10px] font-medium text-gray-500 block truncate max-w-[120px]">
                                                    {order.order_data.pickup_name}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-2 mb-2">
                                        {order.order_products?.map((op: any) => (
                                            <div key={op.uuid} className="flex justify-between text-sm">
                                                <span className="text-gray-600 dark:text-gray-400 font-medium">
                                                    <span className="text-accent font-black mr-2" style={{ color: 'var(--color-accent-500)' }}>{op.quantity}x</span>
                                                    {op.product_details?.name || op.offer_details?.name || op.product_name || 'Producto'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="pt-4 border-t border-gray-100 dark:border-white/5 flex justify-end items-center">
                                        <div className="text-right">
                                            <span className="text-[10px] font-bold text-gray-400 block mb-0.5">Total pagado</span>
                                            <span className="font-black text-lg text-gray-900 dark:text-white">
                                                {currencySymbol}{order.total_amount}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* About Us Section - se muestra al hacer scroll */}
                {showAboutSection && <AboutUsSection tenant={tenant} />}
            </div>

            {/* Bottom Actions */}
            <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent dark:from-[#0b1121] dark:via-[#0b1121]/90 z-20">
                <div className="max-w-md mx-auto">
                    <button
                        onClick={() => navigate(`/simple-menu/${token}`)}
                        className="w-full btn-accent p-5 rounded-[2rem] font-black text-base shadow-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                        style={{ boxShadow: '0 20px 25px -5px var(--color-accent-500)' }}
                    >
                        <ShoppingBag className="w-6 h-6" />
                        Quiero ver el catálogo
                        <ChevronRight className="w-5 h-5 ml-auto opacity-50" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function CheckCircle2(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    )
}
